import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, FileText, Shield, Users, CheckCircle, ExternalLink } from "lucide-react";

const LegalGuidance = () => {
  return (
    <div className="page-container">
      <div className="content-wrapper">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">
            Legal Guidance for Employers
          </h1>
          <p className="text-xl text-muted-foreground">
            Navigating Hiring and Compliance on Second Chance Connect
          </p>
        </div>

        {/* Disclaimer */}
        <Card className="section-card mb-8 border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Important Disclaimer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              Second Chance Connect is a matching service and does not provide legal advice, human resources consulting, or compliance services. The Employer remains solely responsible for all hiring decisions, vetting processes, and comprehensive adherence to all applicable UK employment, anti-discrimination, and data protection legislation. Nothing on this platform should be used as a substitute for professional legal counsel.
            </p>
          </CardContent>
        </Card>

        {/* Section I: Core Principles */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              I. Core Principles of Inclusive Hiring
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              The vast majority of roles in the UK are open to individuals with criminal records. An employer is legally entitled to hire someone with a conviction unless the individual is legally barred from engaging in regulated activity (e.g., working with children or vulnerable adults). Utilizing this platform offers an opportunity to address skills gaps and benefit from the often high loyalty and retention rates seen in employees with convictions.
            </p>

            <div className="pl-4 border-l-4 border-primary/30 space-y-4">
              <div>
                <h3 className="font-semibold mb-2 text-lg">A. Non-Discrimination Obligations</h3>
                <p className="mb-3">
                  Employers must ensure their recruitment practices fully comply with the UK Equality Act 2010 and general non-discrimination principles. Discrimination based on protected characteristics such as race, sex, age (40 or older), religion, or disability is unlawful and will not be tolerated on this Platform.
                </p>
                
                <div className="space-y-3 ml-4">
                  <div>
                    <h4 className="font-medium mb-1">Policy and Reporting:</h4>
                    <p className="text-sm text-muted-foreground">
                      Establish a clear policy stating that discrimination is illegal. Managers should be trained to respond appropriately to complaints, providing for prompt, thorough, and impartial investigation when issues arise.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-1">Reasonable Adjustments:</h4>
                    <p className="text-sm text-muted-foreground">
                      Be prepared to provide reasonable accommodations to applicants or employees for medical or religious reasons, as required by law.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section II: Criminal Record Legislation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              II. Compliance with Criminal Record Legislation (UK)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p>
              Processing information about a person's criminal history requires meticulous compliance with two key pieces of legislation: the Rehabilitation of Offenders Act 1974 (ROA 1974) and the UK General Data Protection Regulation (UK GDPR).
            </p>

            <div className="pl-4 border-l-4 border-primary/30 space-y-6">
              <div>
                <h3 className="font-semibold mb-3 text-lg">A. The Rehabilitation of Offenders Act 1974 (ROA 1974)</h3>
                <p className="mb-4">
                  The ROA 1974 aims to help reformed offenders re-integrate into employment by treating most cautions and convictions as 'spent' after a set rehabilitation period.
                </p>
                
                <div className="space-y-4 ml-4">
                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Spent Convictions
                    </h4>
                    <p className="text-sm">
                      For most roles, an employer cannot legally compel a candidate to disclose a spent conviction. It is unlawful to subject an applicant to "prejudice" (adverse treatment) because of a spent conviction for jobs where the ROA applies.
                    </p>
                  </div>

                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Exempted Roles (DBS Checks)
                    </h4>
                    <p className="text-sm">
                      Access to information about spent convictions (via Standard or Enhanced Disclosure and Barring Service, or DBS checks) is only permissible for roles that are legally exempt from the ROA. You must be legally entitled to ask an individual to reveal their full criminal history.
                    </p>
                  </div>

                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Required Policy</h4>
                    <p className="text-sm">
                      Employers who are eligible to ask candidates about their criminal history (usually due to an ROA exemption) are strongly encouraged by the Disclosure and Barring Service (DBS) to have a written Policy on the Recruitment of Ex-Offenders. This policy helps protect applicants from unfair discrimination based on irrelevant past convictions.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3 text-lg">B. Handling Criminal Offence Data (UK GDPR Article 10)</h3>
                <p className="mb-4">
                  Personal data relating to criminal convictions and offences is subject to separate, stronger safeguards under UK GDPR Article 10 and the Data Protection Act 2018 (DPA 2018).
                </p>
                
                <div className="space-y-4 ml-4">
                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Dual Requirement</h4>
                    <p className="text-sm">
                      You must have both a lawful basis for processing under UK GDPR Article 6 and a specific condition for processing under Article 10 (usually one of the Schedule 1 conditions in the DPA 2018).
                    </p>
                  </div>

                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Explicit Consent</h4>
                    <p className="text-sm">
                      If relying on consent as your condition, it must be explicit, specifying the exact criminal data being processed, and must be confirmed via a clear opt-in mechanism that is separate from general contractual consents.
                    </p>
                  </div>

                  <div className="bg-accent/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Data Security and Retention</h4>
                    <p className="text-sm">
                      Information about an applicant's criminal record must be handled sensitively, recorded carefully, and stored separately and securely on the applicant's file, adhering to UK GDPR guidelines. Retention periods must be based on a clear business need and not exceed the statutory period in which a claim may be brought.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section III: Best Practices */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              III. Best Practices for Assessment and Vetting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="pl-4 border-l-4 border-primary/30 space-y-6">
              <div>
                <h3 className="font-semibold mb-3 text-lg">A. Timing of Checks</h3>
                <p>
                  To ensure the individual is assessed on their merits and without prejudice, best practice dictates that criminal record information should be checked at the conclusion of the recruitment process. This means collecting and reviewing the criminal history information after the initial application, interview, and other pre-employment checking stages.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-3 text-lg">B. Fair Risk Assessment</h3>
                <p className="mb-3">
                  A criminal record check should never be relied on in isolation. When assessing a candidate who has disclosed convictions, you must conduct a careful risk assessment. This assessment should weigh the individual's skills, experience, and ability to perform the role against the nature of the offences disclosed.
                </p>
                
                <p className="mb-2 font-medium">The assessment should consider:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-sm text-muted-foreground">
                  <li>The relevance of the offence to the specific role being offered.</li>
                  <li>The severity and circumstances of the offence.</li>
                  <li>The length of time since the offence occurred and evidence of rehabilitation.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-3 text-lg">C. Responsibility for Vetting</h3>
                <div className="bg-destructive/5 border border-destructive/20 p-4 rounded-lg">
                  <p className="text-sm">
                    The Employer is solely responsible for conducting all necessary vetting, background checks, and identity verification procedures. The Platform makes no guarantee regarding the legitimacy, integrity, or background of any Job Seeker. By using this service, you are contractually agreeing to assume all risk arising from your subsequent use of the candidate's profile and your independent hiring decisions.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section IV: Resources */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              IV. Further Resources and Support
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="mb-4">
              For comprehensive guidance and support on employing people with convictions, Employers are encouraged to utilize resources provided by UK non-profit and statutory bodies:
            </p>

            <div className="grid gap-4">
              <div className="bg-accent/30 p-4 rounded-lg hover:bg-accent/50 transition-colors">
                <h4 className="font-semibold mb-2">Nacro and Unlock</h4>
                <p className="text-sm text-muted-foreground">
                  These charity bodies provide specific advice and support to help both Job Seekers and Employers understand what criminal record information needs to be declared and the rights associated with disclosure.
                </p>
              </div>

              <div className="bg-accent/30 p-4 rounded-lg hover:bg-accent/50 transition-colors">
                <h4 className="font-semibold mb-2">CIPD Trust</h4>
                <p className="text-sm text-muted-foreground">
                  Provides practical recommendations for organisations looking to recruit, employ, and retain people with convictions, including outlining the business benefits.
                </p>
              </div>

              <div className="bg-accent/30 p-4 rounded-lg hover:bg-accent/50 transition-colors">
                <h4 className="font-semibold mb-2">Acas (Advisory, Conciliation and Arbitration Service)</h4>
                <p className="text-sm text-muted-foreground">
                  Offers guidance on employment issues, including criminal record checks.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LegalGuidance;
